import { css } from 'styled-components';

/**
 * Undoes the parts of Astryx's `@layer reset` that break our pre-Astryx styles.
 * That layer holds `reset.css` plus the theme's prose element defaults, which
 * `Theme` injects into it at runtime — so anything in either is fair game here.
 *
 * The discriminator is the **element's own identity**, not where it sits. The
 * UI is a mix: an Astryx `Icon` can appear in a legacy page, and a legacy image
 * can be passed as children to an Astryx `Card`. So neither a DOM region nor a
 * "descendant of Astryx" rule works — only the element itself can say which
 * side it belongs to.
 *
 * Two mechanisms do that, and together they cover every Astryx element:
 *
 * 1. **Layer.** `@layer inq-unreset` sits between `reset` and `astryx-base`, so
 *    any Astryx element whose own StyleX declares the property keeps it —
 *    `Thumbnail`'s image, `Spinner`'s canvas and `CheckboxInput`'s tick are all
 *    `display: block` in `astryx-base` and need nothing here. It also loses
 *    to our unlayered styled-components.
 * 2. **Class.** For the rest, Astryx stamps a stable `astryx-*` class
 *    (`naming.ts`) on the element, which the rules below hand back to the reset
 *    with `revert-layer` — no Astryx values restated, nothing to keep in sync
 *    on an upgrade.
 *
 * Ours use `revert`, which rolls back past the *whole* author origin to the UA
 * default — including our own `inq-reboot` layer. So never revert a property
 * Reboot sets on the same element (`img` vertical-align, `h1`-`h6` font-weight,
 * `hr` border-top): the undo would take ours out with Astryx's.
 *
 * Adding a section: verify in a browser — `revert` does not always do what it
 * reads like (see `[hidden]` below), and no unit test reaches a cascade this
 * deep.
 */
const MEDIA = 'img, svg, video, canvas, audio, iframe, embed, object';

export const AstryxUnresetCss = css`
  @layer inq-unreset {
    /* Media, ours. The reset blockifies every replaced element; ours sit inline
       with text (the PDF badge's emoji is the clearest case).

       [hidden] is skipped, not reverted: an author display declaration outranks
       the UA [hidden] rule, so revert lands on inline, not none.

       height: auto is not undone — width/height attributes are presentational
       hints, which revert skips, so it lands back on auto anyway. */
    :where(${MEDIA}):where(:not([hidden])) {
      display: revert;
      max-width: revert;
    }

    /* img is left out: the reset's vertical-align is middle, which is what
       Reboot asks for anyway. */
    :where(svg, video, canvas, audio, iframe, embed, object) {
      vertical-align: revert;
    }

    /* Media, Astryx's. Matched on the element's own class, so a legacy image
       inside an Astryx container is not caught by it.

       Icon and Markdown need a descendant rule as well, because their stable
       class does not land on the media element. Icon in registry mode renders
       <span class="astryx-icon"> wrapping an svg that carries only the icon
       set's own classes; Markdown puts its class on the root and generates the
       img. Safe as descendants precisely because both produce their media
       rather than accepting it as children, so nothing of ours can be inside
       them — which is not true of any other Astryx component. */
    :where(${MEDIA})[class*='astryx-'],
    [class~='astryx-icon'] :where(svg),
    [class~='astryx-markdown'] :where(img, svg) {
      display: revert-layer;
      vertical-align: revert-layer;
      max-width: revert-layer;
    }
  }
`;
